import { Component, effect } from '@angular/core';
import { toSignal } from '@angular/core/rxjs-interop';
import { NavigationEnd, Router, RouterOutlet } from '@angular/router';
import { filter, map } from 'rxjs';
import { ConfirmDialogModule } from 'primeng/confirmdialog';
import { ToastModule } from 'primeng/toast';
import { AuthService } from './core/auth.service';
import { BrandingService } from './core/branding.service';
import { LocaleService } from './core/locale.service';
import { PushNotificationsService } from './core/push-notifications.service';
import { ErrorBannerComponent } from './shared/error-banner/error-banner.component';
import { SuspendedBannerComponent } from './shared/suspended-banner/suspended-banner.component';
import { WorkflowNoticeBannerComponent } from './shared/workflow-notice-banner/workflow-notice-banner.component';
import { SharedHeaderComponent } from './shared/header/shared-header.component';
import { SidebarComponent } from './shared/sidebar/sidebar.component';
import { LanguageSwitchComponent } from './shared/language-switch/language-switch.component';

/** Pages that stand alone even when someone is signed in: the invitation link is for whoever holds it, so it never shows the signed-in person's sidebar and header (docs/design/39 section 6.1). */
const STANDALONE_PATHS = ['/accept-invitation', '/organizations/claim', '/confirm-registration']; // the pages an emailed link opens: the plain layout, whoever is signed in
const isStandalone = (url: string) => STANDALONE_PATHS.some((p) => url === p || url.startsWith(`${p}?`) || url.startsWith(`${p}/`));

@Component({
  selector: 'app-root',
  imports: [RouterOutlet, SharedHeaderComponent, SidebarComponent, LanguageSwitchComponent, ErrorBannerComponent, SuspendedBannerComponent, WorkflowNoticeBannerComponent, ConfirmDialogModule, ToastModule],
  templateUrl: './app.component.html',
  styleUrl: './app.component.scss'
})
export class AppComponent {
  /** True on a standalone page: the plain layout, whoever is signed in. */
  readonly standalonePage;

  constructor(readonly auth: AuthService, router: Router, locale: LocaleService, branding: BrandingService, pushNotifications: PushNotificationsService) {
    this.standalonePage = toSignal(
      router.events.pipe(
        filter((event): event is NavigationEnd => event instanceof NavigationEnd),
        map((event) => isStandalone(event.urlAfterRedirects))
      ),
      { initialValue: isStandalone(`${window.location.pathname}${window.location.search}`) }
    );
    effect(() => locale.apply(this.auth.currentUser()?.preferredLocale));
    effect(() => (this.auth.currentUser() ? branding.applyForCurrentUser() : branding.reset()));
    effect(() => {
      if (this.auth.currentUser()) {
        void pushNotifications.refreshIfAlreadyGranted();
      }
    });
  }
}
